import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const theme = createTheme({
  palette: {
    primary: { main: "#1C2541" },
    secondary: { main: "#EE8013" },
  },
});

const BlogPost = () => {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);

  const lang = i18n.language?.split("-")[0] || "en";

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setTranslated(null);
    fetch(`https://app.autocrescente.com/WhyNotApi/api/blog/slug/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((data) => setPost(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Update page meta when post loads
  useEffect(() => {
    if (!post) return;
    const c = post[lang]?.title ? post[lang] : post.en || {};
    if (c.metaTitle) document.title = c.metaTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    if (c.metaDescription) meta.setAttribute("content", c.metaDescription);
    return () => { document.title = "Why Not Car Rental"; };
  }, [post, lang]);

  const getContent = () => {
    if (!post) return {};
    const c = post[lang];
    if (c?.title) return c;
    return post.en || {};
  };

  const getCoverUrl = () =>
    post?.coverImage
      ? `https://app.autocrescente.com/WhyNotApi/uploads/blog/${post.coverImage}`
      : null;

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const translateText = async (text) => {
    if (!text?.trim()) return text || "";
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=pt&tl=en&dt=t&q=${encodeURIComponent(text)}`
    );
    const data = await res.json();
    return data[0].map((item) => item[0]).join("");
  };

  const translateHTML = async (html) => {
    if (!html) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent.trim()) nodes.push(node);
    }

    const SEPARATOR = " |||| ";
    const MAX = 3000;
    const batches = [{ nodes: [], text: "" }];
    for (const n of nodes) {
      const last = batches[batches.length - 1];
      const addition = (last.text ? SEPARATOR : "") + n.textContent;
      if (last.text && (last.text + addition).length > MAX) {
        batches.push({ nodes: [n], text: n.textContent });
      } else {
        last.nodes.push(n);
        last.text += addition;
      }
    }

    for (const batch of batches) {
      if (!batch.text) continue;
      const result = await translateText(batch.text);
      const parts = result.split("||||").map((p) => p.trim());
      batch.nodes.forEach((n, i) => { if (parts[i] !== undefined) n.textContent = parts[i]; });
    }

    return doc.body.innerHTML;
  };

  const handleTranslate = async () => {
    if (translated) { setTranslated(null); return; }
    // Use stored English version if available
    if (post?.en?.title) { setTranslated(post.en); return; }
    setTranslating(true);
    try {
      const src = post?.pt || post?.en || {};
      const [title, summary, content] = await Promise.all([
        translateText(src.title),
        translateText(src.summary),
        translateHTML(src.content),
      ]);
      setTranslated({ title, summary, content });
    } catch (e) {
      console.error("Translation failed:", e);
    } finally {
      setTranslating(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ color: "#1C2541" }} />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box textAlign="center" py={14}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Article not found
        </Typography>
        <Link
          component="button"
          onClick={() => navigate("/blog")}
          sx={{ color: "#1C2541", fontWeight: "bold", mt: 2, display: "inline-block" }}
        >
          ← Back to Blog
        </Link>
      </Box>
    );
  }

  const content = getContent();
  const displayContent = translated || content;
  const cover = getCoverUrl();

  return (
    <ThemeProvider theme={theme}>
      {/* Hero cover */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 260, md: 440 },
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#1C2541",
        }}
      >
        {cover && (
          <Box
            component="img"
            src={cover}
            alt={displayContent.title || ""}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, transparent 30%, rgba(28,37,65,0.72) 100%)",
          }}
        />
        {/* Title overlay on hero */}
        <Container
          maxWidth="md"
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            pb: { xs: 3, md: 5 },
          }}
        >
          {post.tags?.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {post.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: "#EE8013",
                    color: "white",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              ))}
            </Stack>
          )}
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            sx={{ color: "white", lineHeight: 1.2, textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}
          >
            {displayContent.title}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            underline="hover"
            onClick={() => navigate("/")}
            sx={{ fontSize: 14, color: "text.secondary", background: "none", border: "none", cursor: "pointer" }}
          >
            Home
          </Link>
          <Link
            component="button"
            underline="hover"
            onClick={() => navigate("/blog")}
            sx={{ fontSize: 14, color: "text.secondary", background: "none", border: "none", cursor: "pointer" }}
          >
            Blog
          </Link>
          <Typography color="text.primary" fontSize={14} noWrap sx={{ maxWidth: 260 }}>
            {content.title}
          </Typography>
        </Breadcrumbs>

        {/* Summary / subtitle */}
        {displayContent.summary && (
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, lineHeight: 1.6, mb: 2 }}
          >
            {displayContent.summary}
          </Typography>
        )}

        {/* Date + translate button */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatDate(post.publishedAt)}
          </Typography>
          <Tooltip title={translated ? "Show original Portuguese" : "Auto-translate to English"}>
            <Button
              size="small"
              variant={translated ? "contained" : "outlined"}
              onClick={handleTranslate}
              disabled={translating}
              startIcon={translating ? <CircularProgress size={14} /> : <TranslateIcon fontSize="small" />}
              sx={{
                borderColor: "#1C2541",
                color: translated ? "white" : "#1C2541",
                backgroundColor: translated ? "#1C2541" : "transparent",
                textTransform: "none",
                fontSize: 12,
                "&:hover": { backgroundColor: translated ? "#263677" : "rgba(28,37,65,0.06)", borderColor: "#1C2541" },
              }}
            >
              {translating ? "Translating…" : translated ? "Show Original" : "Translate to English"}
            </Button>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 5 }} />

        {/* Article HTML content */}
        <Box
          component="article"
          sx={{
            "& h1": { fontSize: "2rem", fontWeight: "bold", color: "#1C2541", mt: 4, mb: 1.5 },
            "& h2": { fontSize: "1.5rem", fontWeight: "bold", color: "#1C2541", mt: 4, mb: 1.5 },
            "& h3": { fontSize: "1.2rem", fontWeight: "bold", color: "#1C2541", mt: 3, mb: 1 },
            "& h4, & h5, & h6": { fontWeight: "bold", color: "#1C2541", mt: 2.5, mb: 1 },
            "& p": { lineHeight: 1.85, mb: 2, color: "#333", fontSize: "1.05rem" },
            "& ul, & ol": { pl: 3, mb: 2 },
            "& li": { mb: 0.75, lineHeight: 1.8, color: "#333", fontSize: "1.05rem" },
            "& img": { maxWidth: "100%", borderRadius: 2, my: 3, display: "block" },
            "& a": { color: "#EE8013", textDecoration: "underline", "&:hover": { color: "#c96a00" } },
            "& blockquote": {
              borderLeft: "4px solid #EE8013",
              pl: 3,
              ml: 0,
              color: "#555",
              fontStyle: "italic",
              my: 3,
              py: 0.5,
            },
            "& code": {
              backgroundColor: "#f5f5f5",
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.9em",
            },
            "& pre": {
              backgroundColor: "#f5f5f5",
              p: 2.5,
              borderRadius: 2,
              overflow: "auto",
              my: 3,
              "& code": { background: "none", p: 0 },
            },
            "& strong": { color: "#1C2541", fontWeight: 700 },
            "& hr": { my: 4, border: "none", borderTop: "1px solid #e0e0e0" },
          }}
          dangerouslySetInnerHTML={{ __html: displayContent.content }}
        />

        <Divider sx={{ mt: 7, mb: 4 }} />

        {/* Back to blog */}
        <Box
          component="button"
          onClick={() => navigate("/blog")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "none",
            border: "none",
            color: "#1C2541",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: 15,
            p: 0,
            "&:hover": { color: "#EE8013" },
            transition: "color 0.15s",
          }}
        >
          <ArrowBackIcon fontSize="small" />
          {t("backToBlog", "Back to Blog")}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default BlogPost;
